import useAuth from "./useAuth";
import {
  PERMISSIONS,
  checkAllPermissions,
  checkAnyPermissions,
  checkAnyPermissionsFromSubtramite,
} from '../permissions';


export const useAuthorization = () => {
  const { user } = useAuth();
  const userPermissions = user?.permissions || [];

  /**
   * Check if user has a specific permission (by permission name string)
   * @param permissionName - The exact permission name (e.g., 'tramites.filter_by_date')
   */
  const hasPermission = (permissionName: string) => {
    return checkAnyPermissions(userPermissions, [permissionName]);
  }

  const hasAllPermissions = (permissions: (keyof typeof PERMISSIONS)[]) => {
    return checkAllPermissions(userPermissions, permissions.map(p => PERMISSIONS[p].name));
  }

  const hasAnyPermissions = (permissions: (keyof typeof PERMISSIONS)[]) => {
    return checkAnyPermissions(userPermissions, permissions.map(p => PERMISSIONS[p].name));
  }

  const hasAnyPermissionsFromSubtramite = (
    permissions: (keyof typeof PERMISSIONS)[],
    tramite: any,
    subtramiteKey: string,
    readOnly: boolean
  ) => {
    return checkAnyPermissionsFromSubtramite(
      user,
      permissions.map(p => PERMISSIONS[p].name),
      tramite,
      subtramiteKey,
      readOnly
    );
  }

  const hasFullAccess = () => {
    return hasAllPermissions(["FULL_ACCESS"]);
  }

  const canManageTramites = hasAllPermissions(["MANAGE_TRAMITES"]);
  const canUploadTramitesFiles = hasAnyPermissions(["MANAGE_TRAMITES", "UPLOAD_TRAMITES_FILES"]);
  const canUploadEstudioTitulo = hasAnyPermissions(["MANAGE_TRAMITES", "UPLOAD_ESTUDIO_TITULO_FILES"]);
  const canManageUsers = hasAnyPermissions(["MANAGE_USERS"]);
  const canManageAuthorization = hasAnyPermissions(["MANAGE_AUTHORIZATION"]);
  const canViewUserPasswords = hasAnyPermissions(["VIEW_USER_PASSWORD"]);

  const canViewDocuments = hasAnyPermissions(["VIEW_DOCUMENTS", "UPLOAD_DOCUMENTS", "MANAGE_DOCUMENTS"]);
  const canUploadDocuments = hasAnyPermissions(["UPLOAD_DOCUMENTS", "MANAGE_DOCUMENTS"]);
  const canManageDocuments = hasAnyPermissions(["MANAGE_DOCUMENTS"]);

  const canUploadPlanosFiles = hasAnyPermissions(["MANAGE_TRAMITES", "UPLOAD_PLANOS_FILES"]);

  const canUploadCertificados = hasAnyPermissions(["MANAGE_TRAMITES", "UPLOAD_CERTIFICADOS_FILES"]);

  const canUploadEscritura = hasAnyPermissions(["UPLOAD_ESCRITURA"]);

  const canEditTitle = hasAnyPermissions(["MANAGE_TRAMITES"]);

  const canManageSystemConfiguration = hasAnyPermissions(["MANAGE_SYSTEM_CONFIGURATION"]);
  const canViewWhatsAppDispatches = hasAnyPermissions(["VIEW_WHATSAPP_DISPATCHES"]);
  const canSendWhatsApp = hasAnyPermissions(["SEND_WHATSAPP"]);
  const canManageWhatsAppTemplates = hasAnyPermissions(["MANAGE_WHATSAPP_TEMPLATES"]);
  const canViewTemporaryLinks = hasAnyPermissions(["VIEW_TEMPORARY_LINKS"]);
  const canDeleteTemporaryLinks = hasAnyPermissions(["DELETE_TEMPORARY_LINKS"]);
  const canAccessContabilidad = hasAnyPermissions(["CONTABILIDAD_VIEW"]);
  const canCreateContabilidad = hasAnyPermissions(["CONTABILIDAD_CREATE"]);
  const canEditContabilidad = hasAnyPermissions(["CONTABILIDAD_EDIT"]);
  const canDeleteContabilidad = hasAnyPermissions(["CONTABILIDAD_DELETE"]);

  const canViewTareas = hasAnyPermissions(["VIEW_TRAMITE_TAREAS", "MANAGE_TRAMITE_TAREAS"]);
  const canManageTareas = hasAnyPermissions(["MANAGE_TRAMITE_TAREAS"]);
  const canManageTareasBase = hasAnyPermissions(["MANAGE_TRAMITE_TAREAS_BASE"]);

  return {
    hasPermission,
    hasAllPermissions,
    hasAnyPermissions,
    hasFullAccess,
    hasAnyPermissionsFromSubtramite,
    canManageTramites,
    canUploadTramitesFiles,
    canUploadEstudioTitulo,
    canManageUsers,
    canManageAuthorization,
    canViewUserPasswords,
    canViewDocuments,
    canUploadDocuments,
    canManageDocuments,
    canUploadPlanosFiles,
    canUploadCertificados,
    canUploadEscritura,
    canEditTitle,
    canManageSystemConfiguration,
    canViewWhatsAppDispatches,
    canSendWhatsApp,
    canManageWhatsAppTemplates,
    canViewTemporaryLinks,
    canDeleteTemporaryLinks,
    canAccessContabilidad,
    canCreateContabilidad,
    canEditContabilidad,
    canDeleteContabilidad,
    canViewTareas,
    canManageTareas,
    canManageTareasBase,
  }
}
